import { useLazyQuery } from '@apollo/client';
import { Box, Checkbox, Radio, Skeleton, styled } from '@mui/material';
import EmptySearchResults from 'Components/EmptySearchResults';
import FlexBox from 'Components/FlexBox';
import Loader from 'Components/Forms/Loader';
import { ThemeTypography } from 'DesignSystem';
import CountryRiskLevel from 'DesignSystem/DataDisplay/CountryRiskLevel/CountryRiskLevel';
import ItemSummary from 'DesignSystem/DataDisplay/ItemSummary/ItemSummary';
import Table from 'DesignSystem/DataDisplay/Table/Table';
import {
  IOrderBy,
  TTableColumn,
  TTableColumnComponent,
} from 'DesignSystem/DataDisplay/Table/table.types';
import { useCompanyPlanContext } from 'FeatureModules/Auth/Contexts/CompanyPlanContext';
import PartnerSkeleton from 'FeatureModules/Partner/Components/Skeleton/PartnerSkeleton';
import { ESiteType, IBaseSite, IClusterSite } from 'FeatureModules/Site/types/site.types';
import {
  getSiteExtraInfo,
  getSiteSecondTitle,
  getSiteTitle,
  SiteTypeLabels,
} from 'FeatureModules/Site/utils/site.utils';
import { GET_SITE } from 'graphql/secure/queries/site.queries';
import useDialog from 'hooks/useDialog';
import React, { FC } from 'react';
import Moment from 'react-moment';
import { Link } from 'react-router-dom';
import { getCountryRiskLevelFromCode } from 'utils/country.utils';
import { removeGraphConnections } from 'utils/graphql.utils';

enum ESiteSelectionTableColumnKeys {
  SELECTION = 'selection',
  TITLE = 'title',
  LAST_EDITED = 'modifiedTimestamp',
  ACTIONS = 'actions',
  COLLAPSABLE = 'collapsable',
}

type TSiteSelectionTableColumnKeys = `${ESiteSelectionTableColumnKeys}`;

interface ISiteSelectionTableProps {
  sites?: IBaseSite[];
  totalCount?: number;

  loading?: boolean;

  /**
   * Only title column and last edited is shown
   */
  previewMode?: boolean;

  hideActions?: boolean;

  /**
   * Shows radio buttons instead of checkboxes for selection.
   */
  singleSelect?: boolean;

  selectedSiteIds?: string[];

  onSiteSelectionToggle?: (siteId: string) => void;

  // TODO: Extend with Table props
  searchTerm?: string | null;
  orderBy?: IOrderBy<TSiteSelectionTableColumnKeys>;
  hasMoreRows?: boolean;
  maxHeight?: number | string;
  onOrderByChange?: (newOrderBy?: IOrderBy<TSiteSelectionTableColumnKeys>) => void;
  onEndReached?: () => void;
  onSearchTermChange?: (value: string) => void;
}

const SITE_SELECTION_TABLE_COLUMNS = [
  {
    columnKey: ESiteSelectionTableColumnKeys.SELECTION,
  },
  {
    columnKey: ESiteSelectionTableColumnKeys.TITLE,
    headerLabel: 'Title',
    sortable: false,
    doubleWidth: true,
  },
  {
    columnKey: ESiteSelectionTableColumnKeys.LAST_EDITED,
    headerLabel: 'Last edited',
    sortable: true,
  },
  { columnKey: ESiteSelectionTableColumnKeys.ACTIONS, headerLabel: 'Actions' },
  { columnKey: ESiteSelectionTableColumnKeys.COLLAPSABLE },
] satisfies TTableColumn<TSiteSelectionTableColumnKeys>[];

const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.custom.colors.almostBlack,
  textDecoration: 'underline',
  '&:hover': {
    textDecoration: 'none',
  },
}));

const TableRowSkeleton = styled(Skeleton)(({ theme }) => ({
  width: `calc(100% + 32px)`,
  height: 68,
  margin: '0 -16px',
  backgroundColor: 'rgba(0, 0, 0, 0.05)',
  borderRadius: 6,
}));

const CustomTableRow: FC<{
  isSelected?: boolean;
  singleSelect?: boolean;
  hideActions?: boolean;
  site: IBaseSite;
  previewMode?: boolean;
  columns: TTableColumnComponent<ESiteSelectionTableColumnKeys>;
  onSelectionChange?: () => void;
  // Navigation close function for links inside the row
  onExit?: () => void;
}> = ({
  site,
  isSelected,
  singleSelect,
  previewMode = false,
  columns: { Selection, Title, ModifiedTimestamp, Actions, Collapsable },
  onSelectionChange,
  onExit,
}) => {
  const { company } = useCompanyPlanContext();
  // Retrieve full site (cluster) fragment to show the first 3 sub sites
  const [loadClusterSites, { data, loading: clusterSitesLoading }] = useLazyQuery<
    {
      site: IClusterSite;
    },
    { id: string }
  >(GET_SITE, { variables: { id: site.id } });

  const countryRiskLevel = getCountryRiskLevelFromCode(site.countryCode);

  const titleColumn = (
    <Title>
      <ItemSummary
        image={site.image}
        imagePlaceholder={
          site.siteType === ESiteType.CLUSTER ? 'site-cluster-placeholder' : 'site-placeholder'
        }
        title={getSiteTitle({
          site,
          farmDisplayOption: company.farmDisplayOption,
          siteDisplayOption: company.siteDisplayOption,
        })}
        externalId={getSiteSecondTitle({
          site,
          farmDisplayOption: company.farmDisplayOption,
          siteDisplayOption: company.siteDisplayOption,
        })}
        extraInfo={getSiteExtraInfo({
          site,
          farmDisplayOption: company.farmDisplayOption,
          siteDisplayOption: company.siteDisplayOption,
        })}
        TitleEndAdornment={countryRiskLevel && <CountryRiskLevel riskLevel={countryRiskLevel} />}
        details={[
          {
            icon: 'tag-filled',
            title: SiteTypeLabels[site.siteType],
          },
          site.siteType === ESiteType.CLUSTER &&
            !!site.sitesType && {
              icon: 'farm',
              title: `${site.sitesCount} ${SiteTypeLabels[site.sitesType].toLowerCase()}${
                site.sitesCount <= 1 ? '' : 's'
              }`,
            },
          site.siteType === ESiteType.CLUSTER &&
            !!site.sitesRawMaterial && {
              icon: 'raw-material',
              title: site.sitesRawMaterial.title,
            },
          site.siteType === ESiteType.FARM &&
            !!site.rawMaterial && {
              icon: 'raw-material',
              title: site.rawMaterial.title,
            },
          {
            icon: 'location',
            title: site.locationName,
          },
        ]}
      />
    </Title>
  );

  const lastEditedColumn = (
    <ModifiedTimestamp>
      <ThemeTypography variant="BODY_MEDIUM">
        <Moment format="MMM DD, YYYY">{site.modifiedTimestamp}</Moment>
      </ThemeTypography>
    </ModifiedTimestamp>
  );

  if (previewMode) {
    return (
      <>
        <Selection />
        {titleColumn}
        {lastEditedColumn}
        <Actions />
      </>
    );
  }

  const handleSelectionChange = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onSelectionChange?.();
  };

  const handleClusterExpanded = () => {
    if (site.siteType === ESiteType.CLUSTER && site.sitesCount && !data?.site.sites) {
      loadClusterSites();
    }
  };

  return (
    <>
      <Selection>
        {singleSelect ? (
          <Radio checked={isSelected} onClick={handleSelectionChange} />
        ) : (
          <Checkbox checked={isSelected} onClick={handleSelectionChange} />
        )}
      </Selection>

      {titleColumn}

      {lastEditedColumn}

      <Actions />

      {site.siteType === ESiteType.CLUSTER && (
        <Collapsable onExpand={handleClusterExpanded}>
          {!!site.sitesCount ? (
            <>
              {clusterSitesLoading || !data?.site.sites ? (
                <FlexBox width="100%" position="relative" height={40}>
                  <Loader size={20} />
                </FlexBox>
              ) : (
                <FlexBox flexDirection="column" alignItems="flex-start" width="100%" gap={2}>
                  <ThemeTypography variant="BODY_MEDIUM">
                    Showing 1-{data.site.sites.edges.length} of {data.site.sitesCount}.{' '}
                    <StyledLink to={`/sites/${site.id}/sites`} onClick={onExit}>
                      See all sites in cluster.
                    </StyledLink>
                  </ThemeTypography>
                  <SiteSelectionTable previewMode sites={removeGraphConnections(data.site.sites)} />
                </FlexBox>
              )}
            </>
          ) : (
            <FlexBox p={0.5}>
              <ThemeTypography variant="BODY_MEDIUM">
                There are no sites in this cluster yet.{' '}
                <StyledLink to={`/sites/${site.id}`} onClick={onExit}>
                  Open cluster overview.
                </StyledLink>
              </ThemeTypography>
            </FlexBox>
          )}
        </Collapsable>
      )}
    </>
  );
};

const SiteSelectionTable: FC<ISiteSelectionTableProps> = ({
  sites,
  searchTerm,
  selectedSiteIds,
  previewMode,
  totalCount,
  singleSelect,
  loading,
  onSiteSelectionToggle,
  ...tableProps
}) => {
  const { closeDialog } = useDialog();

  return (
    <>
      {!previewMode && (
        <FlexBox gap={2}>
          {loading ? (
            <Skeleton width={100} height={18} />
          ) : (
            <>
              <ThemeTypography variant="BODY_MEDIUM_BOLD" color="GRAY_80">
                {sites?.length
                  ? `Showing 1-${sites?.length || 0} of ${totalCount} ${
                      selectedSiteIds && selectedSiteIds?.length > 0
                        ? `(${selectedSiteIds.length} selected)`
                        : ''
                    }`
                  : `No results found${
                      selectedSiteIds?.length ? ` (${selectedSiteIds.length} selected)` : ''
                    }`}
              </ThemeTypography>
            </>
          )}
        </FlexBox>
      )}

      <Table
        {...tableProps}
        searchTerm={searchTerm}
        data-testid="sites"
        hideHeader={previewMode}
        noScrollbarSpacing // For now the whole page stays scrollable until the other dataset table is also scrollable
        columns={SITE_SELECTION_TABLE_COLUMNS}
        disableSelectAll // For now we can not handle select across all pages
      >
        {({ TableRow }) => (
          <>
            {loading &&
              Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={index}>{() => <TableRowSkeleton variant="rectangular" />}</TableRow>
              ))}

            {!loading &&
              !!sites?.length &&
              sites.map(site => (
                <TableRow
                  key={site.id}
                  hasCollapsable={site.siteType === ESiteType.CLUSTER}
                  onClick={() => onSiteSelectionToggle?.(site.id)}
                >
                  {columns => (
                    <CustomTableRow
                      key={`${site.id}-table-row-column`}
                      previewMode={previewMode}
                      singleSelect={singleSelect}
                      isSelected={selectedSiteIds?.includes(site.id)}
                      columns={columns}
                      site={site}
                      onSelectionChange={() => onSiteSelectionToggle?.(site.id)}
                      // Make sure to close the open dialogs when navigating away
                      onExit={closeDialog}
                    />
                  )}
                </TableRow>
              ))}

            {!loading && !sites?.length && (
              <Box position="relative" height={500}>
                <EmptySearchResults
                  message="No search results found"
                  skeletonComponent={<PartnerSkeleton title={searchTerm || ''} />}
                />
              </Box>
            )}
          </>
        )}
      </Table>
    </>
  );
};

export default SiteSelectionTable;
